import React from 'react';
import moment from 'moment';
import { DATE_DISPLAY_FORMAT } from 'services/Dates';
import {extractAndFormatOwners, getAccountDescriptor} from './Accounts';
import { hasSweepRelationship } from 'state/beneficiaries/BeneficiarySelectors';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import groupBy from 'lodash/groupBy';
import {isEmpty, omit, toLower} from 'lodash';
export const SHORT_TERM_PENDING = 2;
export const LONG_TERM_PENDING = 3;
export const PRIMARY_ID = 0;
export const SECONDARY_ID = 1;
export const INDIVIDUAL_ID = 1;
export const ENTITY_ID = 2;
export const BUSINESS_RELATIONSHIP_ID = 105;
import { formatSSN, formatTaxNumber } from 'services/Formats';
import { isBeneficiaryASpouse, newPrimaryBeneficiaryInitialStateV2 } from 'services/Beneficiary';
import BeaconNotification from 'components/shared/BeaconNotification/BeaconNotification';
import Content, { contentConverter } from './Content';
import { capitalizeFirstLetter } from 'services/Validation';
import {isProductEligibleForBeneficiary} from "./Products";
import {beneficiariesEnabled} from "./FeatureFlags";
export const pendingStatusText = {
    2: 'Your updates are pending and undergoing additional processing. Check back in 10 to 15 minutes.',
    3: 'Your updates are pending and undergoing additional processing. Check back in 2-3 business days.'
};


export const BeneV2FieldValidation = {
    FIRST_NAME_REQUIRED: 'First name is required',
    LAST_NAME_REQUIRED: 'Last name is required',
    BUSINESS_ENTITY_NAME_REQUIRED: 'Entity name is required',
    INVALID_FIRST_NAME: 'You have not entered a valid first name',
    INVALID_MIDDLE_NAME: 'You have not entered a valid middle name',
    INVALID_LAST_NAME: 'You have not entered a valid last name',
    INVALID_BUSINESS_ENTITY_NAME: 'You have not entered a valid entity name ',
    INVALID_DATE_OF_BIRTH: 'You have not entered a valid date of birth',
    SOCIAL_SECURITY_NUMBER: 'Social Security number is required',
    INVALID_SOCIAL_SECURITY_NUMBER: 'You have not entered a valid Social Security number',
    INVALID_TIN: 'You have not entered a valid TIN',
    SSN_FORMATTING: '000-00-0000',
    EIN_FORMATTING: '00-0000000',
    RELATIONSHIP_NAME: 'Relationship is required',
    SPOUSE_RELATIONSHIP: 'You’ve already designated a beneficiary identified as a spouse; please select a different option for this relationship',
    ALLOCATION_VALUE_GREATER_ZERO: 'Allocation percentage must be greater than zero',
    INVALID_ALLOCATION_PRIMARY: 'Primary beneficiaries must total 100%',
    INVALID_ALLOCATION_SUCCESSOR: 'Successor beneficiaries must total 100%',
    INVALID_ALLOCATION_SECONDARY: 'Secondary beneficiaries must total 100%'
};

export const beneficiaryAddDisclaimer = "Provide the beneficiary’s full legal name. A beneficiary must be a specific person or entity. Avoid nicknames and initials. " +
    "Designations or instructions such as “to my children equally” or “as per my will” are not valid.";

export const beneficiaryAddDisclaimerTOD = beneficiaryAddDisclaimer + " Estate is not a valid designation for this account type.";
export const beneficiaryRelationshipMap = [
    {
        label: 'Select',
        value: 0
    },
    {
        label: 'Parent',
        value: 100
    },
    {
        label: 'Spouse',
        value: 101
    },
    {
        label: 'Child',
        value: 102
    },
    {
        label: 'Sibling',
        value: 103
    },
    {
        label: 'Entity',
        value: 105
    }
];

export const startCaseWithSymbols = (str) => { //lodash startCase does not handle symbols, this function will preserve symbols in the string.
    return str.split(' ').map(word => {
        return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
};

export const capitalizeName = (name) => {
    if (!name) {
        return '';
    }

    const nameParts = name.split(' ');
    const capitalizedParts = nameParts.map((part) => {
        const upperPart = part.toUpperCase();
        switch (upperPart) {
            case 'SR.':
                return 'Sr.';
            case 'JR.':
                return 'Jr.';
            case "I.":
                return "I";
            case "II.":
                return "II";
            case "III.":
                return "III";
            case "IV.":
                return "IV";
            case "V.":
                return "V";
            case 'I':
            case 'II':
            case 'III':
            case 'IV':
            case 'V':
                return upperPart; // Keep IV and others fully capitalized
            default:
                return startCaseWithSymbols(toLower(part)); // Capitalize first letter of other parts
        }
    });

    const result = capitalizedParts.join(' ').replace(/\s+/g, ' ').trim();
    if (result.endsWith('Sr.') || result.endsWith('Jr.')) {
        return result;
    }
    return result;
};

export const capitalizeBeneficiaryNames = (beneficiary) => {
    return {
        ...beneficiary,
        name: !isNull(beneficiary.name) && !isUndefined(beneficiary.name) ? capitalizeName(beneficiary.name) : beneficiary.name,
        firstName: !isNull(beneficiary.firstName) && !isUndefined(beneficiary.firstName) ? capitalizeName(beneficiary.firstName) : beneficiary.firstName,
        lastName: !isNull(beneficiary.lastName) && !isUndefined(beneficiary.lastName) ? capitalizeName(beneficiary.lastName) : beneficiary.lastName,
        middleName: !isNull(beneficiary.middleName) && !isUndefined(beneficiary.middleName) ? capitalizeName(beneficiary.middleName) : beneficiary.middleName,
        businessEntityName: !isNull(beneficiary.businessEntityName) && !isUndefined(beneficiary.businessEntityName) ? capitalizeName(beneficiary.businessEntityName) : beneficiary.businessEntityName
    };
}

export const formatAccount = (account) => {
    const { beneficiaries, agreements, isEditable, isIraAccount, isEsaAccount,isIndividualAccount,isJointAccount, retirementAccount, beneficiariesMatch, registrationValue } = account;

    const { primary, secondary } = beneficiaries;
    if (!agreements || !agreements.length) {
        return null;
    }
    const pendingUpdateId = findRegistrationPendingId([...primary, ...secondary]);
    const firstAgreement = agreements[0];
    const ownersDisplayName = `(${firstAgreement.ownersFormatted.split(',').map(name => capitalizeName(toLower(name.trim()))).join(', ')})`;
    const primaryBeneficiaries = primary
        .sort((a, b) => b.distributionAmount - a.distributionAmount)
        .map(beneficiary => ({
            ...beneficiary,
            distributionAmountFormated: (beneficiary.distributionAmount * 100).toFixed(2),
            name: capitalizeName(beneficiary.name || beneficiary.businessEntityName),
            distributionAmount: beneficiary.distributionAmount
        }));

    const secondaryBeneficiaries = secondary
        .sort((a, b) => b.distributionAmount - a.distributionAmount)
        .map(beneficiary => ({
            ...beneficiary,
            distributionAmountFormated: (beneficiary.distributionAmount * 100).toFixed(2),
            name: capitalizeName(beneficiary.name || beneficiary.businessEntityName),
            distributionAmount: beneficiary.distributionAmount
        }));

    return {
        displayName: `${account.agreementTypeValue.displayName} ${ownersDisplayName}`,
        displayGroupName: account.agreementTypeValue.displayName,
        beneficiaries: {
            primary: primaryBeneficiaries,
            secondary: secondaryBeneficiaries,
            beneficiaryCount: primaryBeneficiaries.length + secondaryBeneficiaries.length
        },
        agreements,
        registrationValue,
        isEditable,
        isIRA: isIraAccount,
        isEsa: isEsaAccount,
        isIndividual:isIndividualAccount,
        isJoint:isJointAccount,
        retirementAccount,
        beneficiariesMatch,
        pendingText: pendingStatusText[pendingUpdateId] ? pendingStatusText[pendingUpdateId] : null
    };
};
/**
 * Processes a list of account groups to identify and mark those with mismatched beneficiaries and set a flag, while also sorting them alphabetically*/
export const processGroups = (group) => {
    // Group accounts by registrationValue
    const groupedByRegistrationValue = groupBy(group, 'registrationValue');

    //sort groups alphabetically
    const modifiedGroup = group
        .filter(account => !isNull(account.displayName) && !isUndefined(account.displayName))
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map((account) => ({ ...account }));

    // Iterate over each group of accounts with the same registrationValue
    Object.values(groupedByRegistrationValue).forEach(
        (accountsWithSameRegistrationValue) => {
            // Find the first mismatched account
            const firstMismatchedAccount = accountsWithSameRegistrationValue.find(
                (acc) => acc.beneficiariesMatch === false
            );
            // If there is a mismatch, set the displayMismatchBanner flag on the corresponding account in modifiedGroup
            if (firstMismatchedAccount) {
                const index = modifiedGroup.findIndex(
                    (acc) =>
                        acc.registrationValue === firstMismatchedAccount.registrationValue
                );
                if (index !== -1) {
                    modifiedGroup[index].displayMismatchBanner = true;
                }
            }
        }
    );
    return modifiedGroup;
};
const isPrimaryBeneficiary = (beneficiary) => beneficiary.designationId === PRIMARY_ID;
const isSecondaryBeneficiary = (beneficiary) => beneficiary.designationId === SECONDARY_ID;
export const cleanBeneficiaries = (beneficiaries) => {
    return beneficiaries.reduce((acc, bene, index) => {
        const beneficiary = {
            ...bene,
            birthDate: bene.birthDate ? moment.utc(bene.birthDate).format(DATE_DISPLAY_FORMAT) : null,
            businessEntityName: bene.businessEntityNames ? capitalizeFirstLetter(bene.businessEntityNames.join(', ')) : null,
            name: bene.name ? capitalizeFirstLetter(bene.name) : (bene.businessEntityNames ? capitalizeFirstLetter(bene.businessEntityNames.join(', ')) : ""),
            firstName: bene.firstName ? capitalizeFirstLetter(bene.firstName) : "",
            lastName: bene.lastName ? capitalizeFirstLetter(bene.lastName) : "",
            middleName: bene.middleName ? capitalizeFirstLetter(bene.middleName) : "",
            taxId: bene.taxId ? bene.taxId : "",
            _id: index
        };
        if (isPrimaryBeneficiary(bene)) {
            acc.primary.push(beneficiary);
        } else if (isSecondaryBeneficiary(bene)) {
            acc.secondary.push(beneficiary);
        }
        return acc;
    }, { primary: [], secondary: [] });
};

export const mapAgreementsWithOwners = (filteredAgreements, accountsById) => {
    if (!filteredAgreements) {
        return [];
    }

    return filteredAgreements.map((agreement) => {
        const { accountId } = agreement;
        if (!accountId || !accountsById[accountId]) {
            return { ...agreement };
        }

        const {
            owners,
            accountName,
            ticker,
            accountTypeId,
        } = accountsById[accountId];

        return {
            ...agreement,
            accountTypeId,
            owners,
            ownersFormatted: extractAndFormatOwners(accountsById[accountId]),
            accountName,
            ticker
        };
    });
};

export const filterBrokerageAccounts = (linkedBrokerageAccountsMap, accounts, loggedInUserBPID) => {
    return Object.values(linkedBrokerageAccountsMap).filter(
        (brk) =>
            accounts &&
            accounts.length > 0 &&
            brk &&
            !brk.isViewOnly && hasSweepRelationship(brk, accounts, loggedInUserBPID) &&
            brk.accountTypeId === accounts[0].accountTypeId
    );
};

export const findRegistrationPendingId = (beneficiaries) => {
    const beneficiariesPending = beneficiaries.find(
        (bene) => bene.pendingUpdateId === SHORT_TERM_PENDING || bene.pendingUpdateId === LONG_TERM_PENDING
    );
    return beneficiariesPending ? beneficiariesPending.pendingUpdateId : null;
};
export const isBeneficiaryACustomer = (beneficiary) =>
    parseInt(beneficiary.typeId) === 1;
export const getBeneficiaryDisplayBirthdate = (beneficiary) => {
    if (!beneficiary.birthDate) {
        return '-';
    }
    const { birthDate } = beneficiary;
    const separator = '/';
    const format = `MM${separator}DD${separator}YYYY`;
    return moment.utc(birthDate).format(format);
};
export const getBeneficiaryDisplayTaxId = (beneficiary) => {
    if (!beneficiary.taxId) {
        return '-';
    }
    return isBeneficiaryACustomer(beneficiary)
        ? formatSSN(beneficiary.taxId)
        : formatTaxNumber(beneficiary.taxId);
};
export const isBeneficiaryABusinessEntity = (beneficiary) =>
    parseInt(beneficiary.typeId) === 2;

export const renderDesignation = (designationId, isEsa) => {
    if (isEsa) {
        return "successor";
    }
    return designationId === SECONDARY_ID ? "secondary" : "primary";
};
export const renderAllocationDesignation = (designation, isEsa) => {
    if (isEsa) {
        return "Successor";
    }
    return designation;
};
export const renderDesignationHeader = (isEsa) => {
    if (isEsa) {
        return "Successor beneficiaries";
    }
    return "Primary beneficiaries";
};
export const renderAllocationsHeaderDetails = (isEsa) => {
    if (isEsa) {
        return "Your successor beneficiaries are listed below. Review each successor beneficiary and adjust allocation as needed before confirming.";
    }
    return "Your primary and secondary beneficiaries are listed below. Review each beneficiary and adjust allocation as needed before confirming.";
};
export const formatStringIfEducationSavings = (text, isEsa) => { //legal requires that any "beneficiary" or "beneficiaries" have "successor" for any education savings account.
    if (isEsa) {
        return text.replace(/(beneficiar)/gi, (match) => {
            return match.charAt(0) === 'B' ? 'Successor ' + match : 'successor ' + match;
        });
    }
    return text;
};
export const replaceStringIfEducationSavings = (text, isEsa) => { //legal requires that buttons and other places with low space replace beneficiary with successor, if the account is education savings.
    if (isEsa) {
        return text.replace(/Beneficiaries/gi, (match) => {
            return match.charAt(0) === 'B' ? 'Successors' : 'successors';
        }).replace(/Beneficiary/gi, (match) => {
            return match.charAt(0) === 'B' ? 'Successor' : 'successor';
        });
    }
    return text;
};

export const renderRelationshipDropdownHeader = (isEsa) => {
    return isEsa ? "Relationship to Current Beneficiary" : "Relationship";
}

export const educationSavingsWarningNotification = (
    <div className="b-grid-item--12-col">
        <BeaconNotification
            title="Important"
            description={
                <span
                    dangerouslySetInnerHTML={{
                        __html: contentConverter.makeHtml(Content.esaAgeLimitText)
                    }}
                />
            }
            variant="informational"
        />
    </div>
);
export const informationMissingWarningNotification = (
    <div className="b-grid-item--12-col">
        <BeaconNotification
            description={
                <span>
                    Some information is missing for the beneficiary named above. Click Edit to update.
                </span>
            }
            variant="error"
        />
    </div>
);
export const renderType = (typeId) => {
    return typeId === ENTITY_ID ? "Entity" : "Individual";
};

export const typeIsIndividual = (typeId) => typeId === INDIVIDUAL_ID;

export const formatAndHideTaxId = (taxId, typeId) => { // Format and hide some digits of the taxId (for ui display purposes only, NOT data)
    if (!taxId) {
        return null;
    }
    const cleanedTaxId = taxId.replace(/-/g, ''); // Remove existing dashes
    if (typeId === ENTITY_ID) {
        // EIN format, do not hide the last 4 digits
        return cleanedTaxId.replace(/(\d{2})(\d{3})(\d{4})/, "**-****$3");
    }
    // SSN format, hide the first 5 digits
    return cleanedTaxId.replace(/(\d{3})(\d{2})(\d{4})/, "***-**-$3");
};

export const findBeneficiaryById = (id, allBeneficiaries) => {
    const { primary, secondary } = allBeneficiaries;
    const mergedBenes = [...primary, ...secondary];
    return mergedBenes.find(beneficiary => beneficiary._id === id) || null;
};

export const handleBeneFormInputChange = (fieldOrFields, value, setUpdatedBeneficiary, callBack = () => { }) => {
    setUpdatedBeneficiary(prevState => {
        let updatedState;

        if (typeof fieldOrFields === 'string') {
            // Single field update
            updatedState = {
                ...prevState,
                [fieldOrFields]: value
            };
        } else {
            // Multiple fields update
            updatedState = {
                ...prevState,
                ...fieldOrFields
            };
        }

        if (fieldOrFields === 'typeId') {
            updatedState.firstName = newPrimaryBeneficiaryInitialStateV2.firstName;
            updatedState.lastName = newPrimaryBeneficiaryInitialStateV2.lastName;
            updatedState.middleName = newPrimaryBeneficiaryInitialStateV2.middleName;
            updatedState.businessEntityName = newPrimaryBeneficiaryInitialStateV2.businessEntityName;
            updatedState.nameSuffix = newPrimaryBeneficiaryInitialStateV2.nameSuffix;
            updatedState.birthDate = newPrimaryBeneficiaryInitialStateV2.birthDate;
            updatedState.relationshipId = newPrimaryBeneficiaryInitialStateV2.relationshipId;
            updatedState.taxId = newPrimaryBeneficiaryInitialStateV2.taxId;
            callBack();
        }

        if (updatedState.typeId === ENTITY_ID) {
            updatedState.relationshipName = 'Entity';
            updatedState.relationshipId = BUSINESS_RELATIONSHIP_ID;
            updatedState.name = updatedState.businessEntityName;
        }

        if (updatedState.typeId === INDIVIDUAL_ID) {
            updatedState.name = [updatedState.firstName, updatedState.middleName, updatedState.lastName, updatedState.nameSuffix].filter(Boolean).join(' ');
        }
        return updatedState;
    });
};

export const formatSuffix = (suffix) => {
    if (isNull(suffix) || isUndefined(suffix)) {
        return suffix;
    }
    const formattedSuffix = suffix.toUpperCase();
    if (formattedSuffix === 'SR.') {
        return 'Sr.';
    } else if (formattedSuffix === 'JR.') {
        return 'Jr.';
    }
    return suffix;
};

export const faqAnswers = {
    BENEFICIARY_DEFINITION: (
        <>
            A beneficiary is an individual or entity whom you designate to receive your account assets after you pass away. You may name one or more individuals
            (meaning a person) and/or legal entities (such as a trust or a charity) as beneficiaries. You also determine whether each beneficiary is
            a <b>primary</b> or <b>secondary</b> beneficiary.
        </>
    ),
    NAME_YOUR_BENEFICIARIES: 'Naming beneficiaries can provide greater peace of mind and clarifies who inherits your account assets after you pass away.',
    PRIMARY_VS_SECONDARY: (
        <>
            Primary beneficiaries are your first choice to receive all the assets in your account. Generally, secondary beneficiaries would receive your account
            assets only if no primary beneficiaries survived you. <br /><br />
            You may name more than one primary beneficiary and more than one secondary beneficiary and decide how to allocate your account assets among them.
        </>
    ),
    ADD_BENEFICIARIES: (
        <>
            Here is the information you’ll need to add or make changes to your beneficiaries:
            <ul style={{ marginLeft: '20px' }}>
                <li>Individual beneficiaries: first and last name, relationship, and allocation percentage</li>
                <li>Entity beneficiaries: entity name and allocation percentage</li>
            </ul>
            <br />
            While not required, consider providing the optional information about your designated beneficiaries such as Social Security number and/or date of birth.
            That information can help us to verify those individuals more efficiently.
        </>
    ),
    DECIDE_BENEFICIARIES: (
        'If you are unsure as to how to select your beneficiaries, you may wish to speak to an estate planner or your legal or tax professional about your situation.'
    ),
    IRA_BENEFICIARY: (
        'For your T. Rowe Price IRAs, if there are no beneficiaries on your account, your surviving spouse will be considered your sole beneficiary. ' +
        'If you do not have a surviving spouse, your estate will be considered your sole beneficiary. ' +
        'A spouse is any individual who is your spouse under federal law.'
    ),
}

export const faqQuestion = {
    BENEFICIARY_DEFINITION: 'What is a beneficiary?',
    NAME_YOUR_BENEFICIARIES: 'Why is it important to name your beneficiaries?',
    PRIMARY_VS_SECONDARY: 'What is the difference between primary and secondary beneficiaries?',
    ADD_BENEFICIARIES: 'What information do I need to add beneficiaries?',
    DECIDE_BENEFICIARIES: 'How do I decide who my beneficiaries should be?',
    IRA_BENEFICIARY: 'What happens if I don’t designate a beneficiary for my IRA?',
};

export const popoverContent = {
    BENEFICIARY_DEFINITION: {
        title: 'What is the difference between primary and secondary beneficiaries?',
        answer: (
            <>
                Primary beneficiaries are your first choice to receive all the assets in your account. Generally, secondary beneficiaries would receive your account
                assets only if no primary beneficiaries survived you. <br /><br />
                You may name more than one primary beneficiary and more than one secondary beneficiary and decide how to allocate your account assets among them.
            </>
        ),
    },
    DATE_BIRTH:  {
            title: 'Why is providing date of birth useful and important?',
            answer: 'Providing your beneficiaries\' dates of birth can help us to verify those individuals more efficiently.'
    },
    SOCIAL_SECURITY: {
        title: 'Why is providing SSN useful and important?',
        answer: "Providing your beneficiaries' Social Security numbers can help us to verify those individuals more efficiently."
    },
    BENEFICIARY_ALLOCATIONS: {
        title: 'What does it mean to set allocations for your beneficiaries?',
        answer: "When you set allocations for your beneficiaries, you are designating the percentage of your account assets that you would like each beneficiary to inherit."
    },
    ESA_DEFINITION: {
        title: 'Education Savings Account',
        answer: 'A Successor Beneficiary must be a family member (as defined by the Internal Revenue Code) and under age 30 or have special needs. For details on qualifying Successor Beneficiaries, see IRS publication 970 and the T. Rowe Price ESA Disclosure Statement and Custodial Agreement.'
    }
}

export const onRelationshipChange = (handleInputChange) => (e) => {
    const relationshipId = Number(e.target.value);
    const relationshipName = e.target.options[e.target.selectedIndex].text;
    handleInputChange({
        relationshipId,
        relationshipName
    });
};

export const formatSSNtoBeaconField = (ssn) => {
    if (isNull(ssn) || isUndefined(ssn) || ssn.length !== 9) {
        return ssn;
    }
    return ssn.replace(/(\d{3})(\d{2})(\d{4})/, "$1-$2-$3");
}

export const formatEINtoBeaconField = (ein) => {
    if (isNull(ein) || isUndefined(ein) || ein.length !== 9) {
        return ein;
    }
    return ein.replace(/(\d{2})(\d{7})/, "$1-$2");
}

export const beneficiaryRelationshipOptionsV2 = [
    {
        label: 'Spouse',
        value: 101
    },
    {
        label: 'Child',
        value: 102
    },
    {
        label: 'Sibling',
        value: 103
    },
    {
        label: 'Parent',
        value: 100
    },
    {
        label: 'Other',
        value: 104
    }
];

export const beneficiarySuffixOptionsV3 = [
    {
        label: 'Jr.',
        value: 'Jr.'
    },
    {
        label: 'Sr.',
        value: 'Sr.'
    },
    {
        label: 'I',
        value: 'I'
    },
    {
        label: 'II',
        value: 'II'
    },
    {
        label: 'III',
        value: 'III'
    },
    {
        label: 'IV',
        value: 'IV'
    },
    {
        label: 'V',
        value: 'V'
    }
];

export const calculateTotalDistribution = (beneficiaries) => {
    const total = beneficiaries.reduce((acc, bene) => {
        return acc + bene.distributionAmount;
    }, 0);

    return parseFloat(total).toPrecision(6);
};

export const validateTotalDistribution = (totalDistribution) => {
    return totalDistribution < 1 ||
        totalDistribution > 1 ||
        !totalDistribution;
};

export const formatTotalDistribution = (totalDistribution) => {
    const calculate = totalDistribution * 100;
    return Math.round(calculate * 100) / 100;
};

export const validateTotalDistributionFormatted = (totalDistribution) => {
    const totalDist = parseFloat(totalDistribution);
    return !(totalDist < 100 || totalDist > 100 || !totalDist);
};

export const distributeEvenly = (length) => {
    if (length <= 0) {
        return [];
    }

    const evenSplit = (100 / length).toFixed(2);
    const lastSplit = parseFloat((100 - (parseFloat(evenSplit) * (length - 1))).toFixed(2));
    const distribution = [];

    for (let i = 0; i < length; i++) {
        distribution.push(i === length - 1 ? parseFloat(lastSplit) : parseFloat(evenSplit));
    }

    return distribution;
}
export const isSpouseSelected = (beneficiaries) => {
    if (beneficiaries.length > 1) {
        let count = 0;
        beneficiaries.forEach((bene) => {
            if (isBeneficiaryASpouse(bene)) {
                count++;
            }
        });
        return count > 1;
    }
    return false;
};

export const isValidNumber = (value) => {
    if (typeof value !== 'string') {
        return false;
    }

    // Regular expression to match a valid number format
    const numberRegex = /^[0-9]+(\.[0-9]{1,2})?$/;

    return numberRegex.test(value);
}

export function getDisplayName({ name, firstName, middleName, lastName, nameSuffix }) {
    return name ? name : [firstName, middleName, lastName, nameSuffix].filter(Boolean).join(' ');
}

const cleanTaxId = (taxId) => {
    return taxId ? taxId.replace(/-/g, '') : taxId;
};

const SUFFIX_DEFAULT_VALUE = '- -';

export const generateBeneficiaryPayloadV2 = (
  accounts,
  previousBeneficiaries,
  beneficiaries
) => {
    const benePayload = [];
    if (isEmpty(beneficiaries)) {
        accounts.forEach((account) => {
            previousBeneficiaries.forEach((bene) => {
                const beneDistributionRounded = Number(parseFloat(bene.distributionAmount).toFixed(4));
                let beneWithAccountDescriptor = {
                    ...bene,
                    distributionAmount: beneDistributionRounded,
                    accountDescriptor: getAccountDescriptor(account)
                };
                const cleanedBeneficiary = omit(beneWithAccountDescriptor, [
                    '_id',
                    'businessEntityName',
                    'deleted'
                ]);

                benePayload.push(cleanedBeneficiary);
            });
        });
        return benePayload;
    }

    // filter accounts to add beneficiary for eligible product types
    accounts.filter(isProductEligibleForBeneficiary).forEach((account) => {
        beneficiaries.forEach((bene) => {
            const beneDistributionRounded = Number(parseFloat(bene.distributionAmount).toFixed(4));
            let beneWithAccountDescriptor = {
                ...bene,
                distributionAmount: beneDistributionRounded,
                accountDescriptor: getAccountDescriptor(account)
            };
            if (isBeneficiaryABusinessEntity(bene)) {
                //Need to put businessEntityNames in an array
                beneWithAccountDescriptor['businessEntityNames'] = [
                    bene.businessEntityName
                ];
            }
            const cleanedBeneficiary = omit(beneWithAccountDescriptor, [
                '_id',
                'businessEntityName',
                'deleted',
                'distributionAmountFormated',
                'name',
                'relationshipName',
            ]);

            //ensure typeId value is an integer
            cleanedBeneficiary['typeId'] = parseInt(cleanedBeneficiary['typeId']);
            if(beneficiariesEnabled()) {
                //if in new benes page, ensure taxId is valid
                cleanedBeneficiary['taxId'] = cleanTaxId(cleanedBeneficiary['taxId']);
            }
            if (
              cleanedBeneficiary.nameSuffix &&
              cleanedBeneficiary.nameSuffix === SUFFIX_DEFAULT_VALUE
            ) {
                //remove default value on suffix and set to empty string
                cleanedBeneficiary['nameSuffix'] = '';
            }
            benePayload.push(cleanedBeneficiary);
        });
    });

    return benePayload;
};

export const generateBenficiaryUpdatePayloadV2 = (accounts, beneficiaries) => {
    const benePayload = [];
    const filterDeleted = beneficiaries.filter((bene) => !bene.deleted);
    filterDeleted.forEach((bene) => {
        const beneDistributionRounded = Number(parseFloat(bene.distributionAmount).toFixed(4));
        let beneWithAccountDescriptor = {
            ...bene,
            distributionAmount: beneDistributionRounded,
            accountDescriptor: getAccountDescriptor(accounts[0])
        };
        if (isBeneficiaryABusinessEntity(bene)) {
            //Need to put businessEntityNames in an array
            beneWithAccountDescriptor['businessEntityNames'] = [
                bene.businessEntityName
            ];
        }
        const cleanedBeneficiary = omit(beneWithAccountDescriptor, [
            '_id',
            'businessEntityName',
            'deleted',
            'distributionAmountFormated',
            'name',
            'relationshipName',
        ]);

        //ensure typeId value is an integer
        cleanedBeneficiary['typeId'] = parseInt(cleanedBeneficiary['typeId']);
        cleanedBeneficiary['taxId'] = cleanedBeneficiary['taxId']
          ? cleanedBeneficiary['taxId'].replaceAll('-', '')
          : null;
        if (
          cleanedBeneficiary.nameSuffix &&
          cleanedBeneficiary.nameSuffix === SUFFIX_DEFAULT_VALUE
        ) {
            //remove default value on suffix and set to empty string
            cleanedBeneficiary['nameSuffix'] = '';
        }
        benePayload.push(cleanedBeneficiary);
    });

    return benePayload;
};

export const isLastPrimaryBeneficiary = (primaryBeneficiaries, designationId) => { //checks if a beneficiary is the last primary beneficiary, in which case the designation radio buttons would be disabled
    if (isNull(primaryBeneficiaries) || isUndefined(primaryBeneficiaries) || isNull(designationId) || isUndefined(designationId)) {
        return true;
    }
    if (primaryBeneficiaries.length === 0) {
        return true;
    }
    if (primaryBeneficiaries.length === 1 && primaryBeneficiaries[0]._id === designationId) {
        return true;
    }
    return false;
};

export const isMissingBeneficiaryInformation = (beneficiary) => { //checks if a beneficiary is missing required data (name and relationship data)
    if (isNull(beneficiary) || isUndefined(beneficiary)) {
        return true;
    }
    const {businessEntityName, firstName, lastName, relationshipId, relationshipName, typeId} = beneficiary;
    if (isEmpty(relationshipName) || isNull(relationshipId) || isUndefined(relationshipId)) { //ALL benes must have a relationship
        return true;
    }
    if (typeId === INDIVIDUAL_ID) {
        return (isEmpty(firstName) || isEmpty(lastName)); //INDIVIDUAL benes must have first name and last name
    }
    else if (typeId === ENTITY_ID) { //ENTITY benes must have a business entity name
        return (isEmpty(businessEntityName));
    }
    return true; //a bene typeId must be either individual or entity, if it is neither, the data is bad
}

export const validateBeneficiariesData = (beneficiaries) =>
  Array.isArray(beneficiaries) && beneficiaries.every(beneficiary => !isMissingBeneficiaryInformation(beneficiary));
